<!--
  SubscriptionPackagesDialog

  Opened from Schools.vue with:  this.$refs.subscriptionPackagesDialog.open(school)

  How subjects work:
    - A PACKAGE has a list of subjects (what the school paid for).
    - Each USER on the package is ALLOCATED some of those subjects.
    - You can only allocate subjects that are on the package, and if a subject
      is later removed from the package, users lose it too.
-->
<template>
  <div>
    <!-- 1. THE SCHOOL'S PACKAGES -->
    <v-dialog persistent v-model="packagesDialog" max-width="1100px" scrollable>
      <v-card>
        <v-card-title>
          <span class="text-h5">Subscription Packages</span>
          <span v-if="school" class="text-h6 grey--text ml-2">
            {{ school.name }}
          </span>
          <v-spacer />
          <v-btn color="primary" :disabled="offline || loadingPackages || packages?.length > 0" @click="openNewPackageForm">
            New Package
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-data-table
            :headers="packageTableHeaders"
            :items="packages"
            :loading="loadingPackages"
            loading-text="Loading packages..."
            no-data-text="No packages for this school yet"
            :footer-props="{ showFirstLastPage: true }"
            sort-by="name"
          >
            <template v-slot:[`item.subjectIDs`]="{ item: subscriptionPackage }">
              {{ (subscriptionPackage.subjectIDs || []).length }}
            </template>

            <template v-slot:[`item.active`]="{ item: subscriptionPackage }">
              <v-icon v-if="subscriptionPackage.active !== false" small color="success">
                mdi-check-circle
              </v-icon>
              <v-icon v-else small color="grey">mdi-minus-circle-outline</v-icon>
            </template>

            <template v-slot:[`item.actions`]="{ item: subscriptionPackage }">
              <v-icon
                small
                class="mr-2"
                title="Users on this package"
                :disabled="offline"
                @click="openUsersDialog(subscriptionPackage)"
              >
                mdi-account-multiple-plus
              </v-icon>
              <v-icon
                small
                class="mr-2"
                title="Edit package"
                :disabled="offline"
                @click="openEditPackageForm(subscriptionPackage)"
              >
                mdi-pencil
              </v-icon>
              <v-icon
                small
                title="Delete package"
                :disabled="offline"
                @click="openDeletePackageDialog(subscriptionPackage)"
              >
                mdi-delete
              </v-icon>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" @click="packagesDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 2. CREATE / EDIT A PACKAGE -->
    <v-dialog persistent v-model="packageFormDialog" max-width="640px">
      <v-card>
        <v-form ref="packageForm" v-model="packageFormValid">
          <v-card-title>
            <span class="text-h5">
              {{ packageForm.id ? "Edit Package" : "New Package" }}
            </span>
          </v-card-title>

          <v-card-text>
            <v-text-field
              v-model="packageForm.name"
              label="Package name"
              :rules="[(name) => !!name || 'Name is required']"
            ></v-text-field>

            <v-select
              v-model="packageForm.tier"
              :items="packageTiers"
              label="Tier"
              clearable
            ></v-select>

            <v-autocomplete
              v-model="packageForm.subjectIDs"
              :items="sortedSubjects"
              :item-text="subjectLabel"
              item-value="id"
              :filter="subjectMatchesSearch"
              :loading="loadingSubjects"
              label="Subjects in this package"
              hint='Search with any words, e.g. "4 maths"'
              persistent-hint
              multiple
              small-chips
              deletable-chips
              clearable
            ></v-autocomplete>

            <v-switch v-model="packageForm.active" label="Active"></v-switch>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn color="primary" text @click="packageFormDialog = false">
              Cancel
            </v-btn>
            <v-btn
              color="primary"
              :disabled="!packageFormValid"
              :loading="savingPackage"
              @click="savePackage"
            >
              Save
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <!-- 3. USERS ON A PACKAGE -->
    <v-dialog persistent v-model="usersDialog" max-width="1000px" scrollable>
      <v-card>
        <v-card-title>
          <span class="text-h5">Users</span>
          <span v-if="usersPackage" class="text-h6 grey--text ml-2">
            {{ usersPackage.name }}
          </span>
        </v-card-title>

        <v-card-text>
          <v-alert v-if="usersPackageSubjects.length === 0" type="warning" dense text>
            This package has no subjects yet. Edit the package and add subjects
            before allocating them to users.
          </v-alert>

          <!-- Adding users -->
          <v-row>
            <v-col cols="12" md="6">
              <v-combobox
                v-model="emailsToAdd"
                :items="knownEmails"
                :loading="loadingKnownEmails"
                label="Email addresses to add"
                hint="Pick existing accounts, or type/paste emails and press enter"
                persistent-hint
                multiple
                small-chips
                deletable-chips
                clearable
              ></v-combobox>
            </v-col>

            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="subjectsToAllocate"
                :items="usersPackageSubjects"
                :item-text="subjectLabel"
                item-value="id"
                :filter="subjectMatchesSearch"
                label="Subjects to allocate to them"
                hint="Only this package's subjects can be allocated"
                persistent-hint
                multiple
                small-chips
                deletable-chips
                clearable
              ></v-autocomplete>
            </v-col>
          </v-row>

          <div class="d-flex justify-end mt-2">
            <v-btn
              color="primary"
              :disabled="offline || emailsToAdd.length === 0"
              :loading="addingUsers"
              @click="addUsers"
            >
              Add Users
            </v-btn>
          </div>

          <v-alert
            v-if="usersMessage"
            :type="usersMessageIsError ? 'error' : 'success'"
            dense
            text
            class="mt-4 mb-0"
          >
            {{ usersMessage }}
          </v-alert>

          <!-- Users already on the package -->
          <v-data-table
            class="mt-4"
            :headers="accessTableHeaders"
            :items="accessRows"
            :loading="loadingAccessRows"
            loading-text="Loading users..."
            no-data-text="Nobody is on this package yet"
            :footer-props="{ showFirstLastPage: true }"
            sort-by="email"
          >
            <template v-slot:[`item.subjectIDs`]="{ item: accessRow }">
              <v-autocomplete
                v-model="accessRow.subjectIDs"
                :items="usersPackageSubjects"
                :item-text="subjectLabel"
                item-value="id"
                :filter="subjectMatchesSearch"
                :loading="savingAccessRowID === accessRow.id"
                :disabled="offline || savingAccessRowID === accessRow.id"
                placeholder="Nothing allocated"
                multiple
                small-chips
                deletable-chips
                hide-details
                dense
                class="my-2"
                style="min-width: 300px"
                @change="saveAccessRow(accessRow)"
              ></v-autocomplete>
            </template>

            <template v-slot:[`item.active`]="{ item: accessRow }">
              <v-switch
                v-model="accessRow.active"
                :disabled="offline || savingAccessRowID === accessRow.id"
                dense
                hide-details
                class="mt-0"
                @change="saveAccessRow(accessRow)"
              ></v-switch>
            </template>

            <template v-slot:[`item.actions`]="{ item: accessRow }">
              <v-icon
                small
                title="Remove from package"
                :disabled="offline"
                @click="removeAccessRow(accessRow)"
              >
                mdi-delete
              </v-icon>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" @click="closeUsersDialog">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 4. CONFIRM DELETING A PACKAGE -->
    <v-dialog persistent v-model="deletePackageDialog" max-width="500px">
      <v-card>
        <v-card-title class="justify-center text-center">
          Delete this package and remove everyone on it?
        </v-card-title>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" text @click="deletePackageDialog = false">
            Cancel
          </v-btn>
          <v-btn color="primary" :loading="deletingPackage" @click="deletePackage">
            Delete
          </v-btn>
          <v-spacer />
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import { API } from "aws-amplify";
import {
  subscriptionPackagesBySchool,
  subscriptionAccessesBySubscriptionPackage,
} from "@/graphql/queries";
import {
  createSubscriptionPackage,
  updateSubscriptionPackage,
  deleteSubscriptionPackage,
  createSubscriptionAccess,
  updateSubscriptionAccess,
  deleteSubscriptionAccess,
} from "@/graphql/mutations";
import { parseGraphqlResult } from "@/core/utils/parseGraphqlResult";
import { cleanEmail, emailKey } from "@/core/utils/subscriptionAccess";

const AUTH_MODE = "AMAZON_COGNITO_USER_POOLS";

// Emails of existing accounts, for the "add users" suggestions. These come
// straight from Cognito, so their casing matches what the SubscriptionAccess
// owner auth rule compares against.
const LIST_REPORTING_TEACHER_EMAILS = /* GraphQL */ `
  query ListReportingTeacherEmails($nextToken: String) {
    listReportingTeachers(limit: 1000, nextToken: $nextToken) {
      items {
        id
        email
        _deleted
      }
      nextToken
    }
  }
`;

// Runs a list query page by page and returns every item from every page.
async function fetchAllPages(query, variables, resultField) {
  let allItems = [];
  let nextToken = null;

  do {
    const result = await API.graphql({
      query,
      variables: { ...variables, nextToken },
      authMode: AUTH_MODE,
    });

    const page = result.data[resultField];
    allItems = allItems.concat(page.items);
    nextToken = page.nextToken;
  } while (nextToken);

  return allItems.filter((item) => item && !item._deleted);
}

export default {
  name: "SubscriptionPackagesDialog",

  data: () => ({
    // 1. The school's packages
    packagesDialog: false,
    school: null,
    packages: [],
    loadingPackages: false,
    packageTableHeaders: [
      { text: "Name", value: "name" },
      { text: "Tier", value: "package" },
      { text: "Subjects", value: "subjectIDs" },
      { text: "Active", value: "active" },
      { text: "Actions", value: "actions", sortable: false },
    ],

    // 2. Create / edit a package
    packageFormDialog: false,
    packageFormValid: false,
    savingPackage: false,
    packageTiers: ["Basic", "Pro", "Premium"],
    packageForm: {
      id: null,
      _version: null,
      name: "",
      tier: null,
      subjectIDs: [],
      active: true,
    },

    // 3. Users on a package
    usersDialog: false,
    usersPackage: null,
    accessRows: [],
    loadingAccessRows: false,
    savingAccessRowID: null,
    emailsToAdd: [],
    subjectsToAllocate: [],
    addingUsers: false,
    usersMessage: "",
    usersMessageIsError: false,
    accessTableHeaders: [
      { text: "Email", value: "email" },
      { text: "Allocated subjects", value: "subjectIDs", sortable: false },
      { text: "Active", value: "active", sortable: false },
      { text: "Actions", value: "actions", sortable: false },
    ],

    // 4. Delete a package
    deletePackageDialog: false,
    packageToDelete: null,
    deletingPackage: false,

    // Lookup data
    knownEmails: [],
    loadingKnownEmails: false,
  }),

  computed: {
    ...mapState({
      loadingSubjects: (state) => state.loadingSubjects,
      offline: (state) => !state.isOnline,
    }),

    ...mapGetters(["sortedSubjects"]),

    // The subjects on the package whose users we're managing. These are the
    // ONLY subjects that can be allocated to its users.
    usersPackageSubjects() {
      const packageSubjectIDs = this.usersPackage?.subjectIDs || [];
      return this.sortedSubjects.filter((subject) =>
        packageSubjectIDs.includes(subject.id),
      );
    },
  },

  methods: {
    // =======================================================================
    // Subject display + search
    // =======================================================================
    subjectLabel(subject) {
      if (!subject) return "";
      return `Gr.${subject.grade} - ${subject.subject}`;
    },

    // Every word typed must appear somewhere in the grade or subject name,
    // in any order. "4 maths", "maths 4" and "gr 4 mat" all work.
    subjectMatchesSearch(subject, searchText) {
      const subjectText = this.subjectLabel(subject).toLowerCase();
      const searchWords = (searchText || "").toLowerCase().split(" ");

      return searchWords.every((word) => subjectText.includes(word));
    },

    // =======================================================================
    // Opening (called from Schools.vue)
    // =======================================================================
    open(school) {
      this.school = school;
      this.packages = [];
      this.packagesDialog = true;

      this.loadPackages();
      if (this.sortedSubjects.length === 0) this.$store.dispatch("fetchSubjects");
      if (this.knownEmails.length === 0) this.loadKnownEmails();
    },

    // =======================================================================
    // Loading
    // =======================================================================
    async loadPackages() {
      if (!this.school?.id) return;

      this.loadingPackages = true;
      try {
        const packages = await fetchAllPages(
          subscriptionPackagesBySchool,
          { schoolID: this.school.id },
          "subscriptionPackagesBySchool",
        );
        this.packages = parseGraphqlResult(packages);
      } catch (error) {
        console.log("loadPackages error:", error);
      } finally {
        this.loadingPackages = false;
      }
    },

    // async loadSubjects() {
    //   this.loadingSubjects = true;
    //   try {
    //     this.subjects = await fetchAllPages(LIST_SUBJECTS, {}, "listSubjects");
    //   } catch (error) {
    //     console.log("loadSubjects error:", error);
    //   } finally {
    //     this.loadingSubjects = false;
    //   }
    // },

    async loadKnownEmails() {
      this.loadingKnownEmails = true;
      try {
        const teachers = await fetchAllPages(
          LIST_REPORTING_TEACHER_EMAILS,
          {},
          "listReportingTeachers",
        );

        const emails = new Set();
        for (const teacher of teachers) {
          if (teacher.email) emails.add(cleanEmail(teacher.email));
        }
        this.knownEmails = [...emails].sort();
      } catch (error) {
        console.log("loadKnownEmails error:", error);
      } finally {
        this.loadingKnownEmails = false;
      }
    },

    async fetchAccessRowsForPackage(subscriptionPackageID) {
      const accessRows = await fetchAllPages(
        subscriptionAccessesBySubscriptionPackage,
        { subscriptionPackageID },
        "subscriptionAccessesBySubscriptionPackage",
      );
      return parseGraphqlResult(accessRows);
    },

    async loadAccessRows() {
      if (!this.usersPackage?.id) return;

      this.loadingAccessRows = true;
      try {
        const accessRows = await this.fetchAccessRowsForPackage(
          this.usersPackage.id,
        );
        this.accessRows = accessRows.map((accessRow) =>
          this.prepareAccessRowForTable(accessRow),
        );
      } catch (error) {
        console.log("loadAccessRows error:", error);
      } finally {
        this.loadingAccessRows = false;
      }
    },

    // Drops allocated subjects that are no longer on the package (so the table
    // shows what the user actually gets), and turns a missing `active` into true.
    prepareAccessRowForTable(accessRow) {
      const packageSubjectIDs = this.usersPackage?.subjectIDs || [];

      return {
        ...accessRow,
        subjectIDs: (accessRow.subjectIDs || []).filter((subjectID) =>
          packageSubjectIDs.includes(subjectID),
        ),
        active: accessRow.active !== false,
      };
    },

    // =======================================================================
    // Create / edit a package
    // =======================================================================
    openNewPackageForm() {
      this.packageForm = {
        id: null,
        _version: null,
        name: "",
        tier: null,
        subjectIDs: [],
        active: true,
      };

      if (this.$refs.packageForm) this.$refs.packageForm.resetValidation();
      this.packageFormDialog = true;
    },

    openEditPackageForm(subscriptionPackage) {
      this.packageForm = {
        id: subscriptionPackage.id,
        _version: subscriptionPackage._version,
        name: subscriptionPackage.name || "",
        tier: subscriptionPackage.package || null,
        subjectIDs: [...(subscriptionPackage.subjectIDs || [])],
        active: subscriptionPackage.active !== false,
      };
      this.packageFormDialog = true;
    },

    async savePackage() {
      if (!this.$refs.packageForm.validate()) return;

      this.savingPackage = true;
      try {
        const packageFields = {
          schoolID: this.school.id,
          name: this.packageForm.name.trim(),
          package: this.packageForm.tier || null,
          subjectIDs: this.packageForm.subjectIDs || [],
          active: this.packageForm.active,
        };

        if (this.packageForm.id) {
          await API.graphql({
            query: updateSubscriptionPackage,
            variables: {
              input: {
                id: this.packageForm.id,
                _version: this.packageForm._version,
                ...packageFields,
              },
            },
            authMode: AUTH_MODE,
          });
        } else {
          await API.graphql({
            query: createSubscriptionPackage,
            variables: { input: packageFields },
            authMode: AUTH_MODE,
          });
        }

        this.packageFormDialog = false;
        await this.loadPackages();
      } catch (error) {
        console.log("savePackage error:", error);
      } finally {
        this.savingPackage = false;
      }
    },

    // =======================================================================
    // Delete a package (and everyone's access to it)
    // =======================================================================
    openDeletePackageDialog(subscriptionPackage) {
      this.packageToDelete = subscriptionPackage;
      this.deletePackageDialog = true;
    },

    async deletePackage() {
      if (!this.packageToDelete) return;

      this.deletingPackage = true;
      try {
        const accessRows = await this.fetchAccessRowsForPackage(
          this.packageToDelete.id,
        );

        for (const accessRow of accessRows) {
          await API.graphql({
            query: deleteSubscriptionAccess,
            variables: {
              input: { id: accessRow.id, _version: accessRow._version },
            },
            authMode: AUTH_MODE,
          });
        }

        await API.graphql({
          query: deleteSubscriptionPackage,
          variables: {
            input: {
              id: this.packageToDelete.id,
              _version: this.packageToDelete._version,
            },
          },
          authMode: AUTH_MODE,
        });

        this.deletePackageDialog = false;
        this.packageToDelete = null;
        await this.loadPackages();
      } catch (error) {
        console.log("deletePackage error:", error);
      } finally {
        this.deletingPackage = false;
      }
    },

    // =======================================================================
    // Users on a package
    // =======================================================================
    async openUsersDialog(subscriptionPackage) {
      this.usersPackage = subscriptionPackage;
      this.accessRows = [];
      this.emailsToAdd = [];
      this.usersMessage = "";

      // New users get every package subject by default. Remove chips to narrow it.
      this.subjectsToAllocate = [...(subscriptionPackage.subjectIDs || [])];

      this.usersDialog = true;
      await this.loadAccessRows();
    },

    closeUsersDialog() {
      this.usersDialog = false;
      this.usersPackage = null;
      this.accessRows = [];
      this.emailsToAdd = [];
      this.usersMessage = "";
    },

    // The combobox can hold picked emails AND pasted text like
    // "a@school.co.za, b@school.co.za", so split everything into single emails
    // and drop duplicates.
    getEmailsToAdd() {
      const emails = [];

      for (const entry of this.emailsToAdd) {
        const pieces = String(entry).split(/[\s,;]+/);

        for (const piece of pieces) {
          const email = cleanEmail(piece);
          const looksLikeEmail = email.includes("@") && email.includes(".");
          const alreadyListed = emails.some(
            (listedEmail) => emailKey(listedEmail) === emailKey(email),
          );

          if (looksLikeEmail && !alreadyListed) emails.push(email);
        }
      }

      return emails;
    },

    isAlreadyOnPackage(email) {
      return this.accessRows.some(
        (accessRow) => emailKey(accessRow.email) === emailKey(email),
      );
    },

    async addUsers() {
      const emails = this.getEmailsToAdd();

      if (emails.length === 0) {
        this.usersMessageIsError = true;
        this.usersMessage = "No valid email addresses to add.";
        return;
      }

      const newEmails = emails.filter((email) => !this.isAlreadyOnPackage(email));
      const alreadyOnPackageCount = emails.length - newEmails.length;

      this.addingUsers = true;
      this.usersMessage = "";
      try {
        for (const email of newEmails) {
          await API.graphql({
            query: createSubscriptionAccess,
            variables: {
              input: {
                email,
                subscriptionPackageID: this.usersPackage.id,
                subjectIDs: this.subjectsToAllocate,
                active: true,
              },
            },
            authMode: AUTH_MODE,
          });
        }

        this.emailsToAdd = [];
        await this.loadAccessRows();

        this.usersMessageIsError = false;
        this.usersMessage = `Added ${newEmails.length} user(s).`;
        if (alreadyOnPackageCount > 0) {
          this.usersMessage += ` ${alreadyOnPackageCount} were already on this package.`;
        }
      } catch (error) {
        console.log("addUsers error:", error);
        this.usersMessageIsError = true;
        this.usersMessage = "Something went wrong adding users.";
      } finally {
        this.addingUsers = false;
      }
    },

    // Saves a row's allocated subjects + active switch. The row's inputs are
    // disabled while this runs, so two quick edits can't race each other.
    async saveAccessRow(accessRow) {
      this.savingAccessRowID = accessRow.id;
      try {
        const result = await API.graphql({
          query: updateSubscriptionAccess,
          variables: {
            input: {
              id: accessRow.id,
              _version: accessRow._version,
              subjectIDs: accessRow.subjectIDs,
              active: accessRow.active,
            },
          },
          authMode: AUTH_MODE,
        });

        const savedRow = this.prepareAccessRowForTable(
          parseGraphqlResult(result.data.updateSubscriptionAccess),
        );

        this.accessRows = this.accessRows.map((row) =>
          row.id === savedRow.id ? savedRow : row,
        );
      } catch (error) {
        console.log("saveAccessRow error:", error);
        await this.loadAccessRows();
      } finally {
        this.savingAccessRowID = null;
      }
    },

    async removeAccessRow(accessRow) {
      try {
        await API.graphql({
          query: deleteSubscriptionAccess,
          variables: {
            input: { id: accessRow.id, _version: accessRow._version },
          },
          authMode: AUTH_MODE,
        });

        this.accessRows = this.accessRows.filter((row) => row.id !== accessRow.id);
      } catch (error) {
        console.log("removeAccessRow error:", error);
      }
    },
  },
};
</script>